import { getAllPassos, type PassoJornada } from '@content/jornada.js'
import { getTopicoContent } from '@content/topicos-content.js'
import type { TopicoProgresso } from '@services/db.js'
import type { ModuloChecklist } from '@hooks/useChecklistGeral.js'

export interface ChecklistItemPendente {
  id: string
  texto: string
}

export interface PassoChecklistPendente {
  passo: PassoJornada
  itensPendentes: ChecklistItemPendente[]
  total: number
  concluidos: number
  percentual: number
}

export interface ModuloChecklistPendente {
  moduloId: string
  moduloTitulo: string
  moduloIcone: string
  moduloOrdem: number
  passos: PassoChecklistPendente[]
  totalPendentes: number
}

function mergeChecklist(
  contentItems: { id: string; texto: string }[],
  dbItems: { id: string; texto: string; concluido: boolean }[],
): { id: string; texto: string; concluido: boolean }[] {
  const dbMap = new Map(dbItems.map((i) => [i.id, i.concluido]))
  return contentItems.map((ci) => ({
    id: ci.id,
    texto: ci.texto,
    concluido: dbMap.get(ci.id) ?? false,
  }))
}

/** Mesma regra do passo atual: primeiro em-andamento, senão primeiro não iniciado. */
export function getPassoAtualTopicoId(modulos: ModuloChecklist[]): string | null {
  const modulosOrdenados = [...modulos].sort((a, b) => a.ordem - b.ordem)

  for (const modulo of modulosOrdenados) {
    const topicosOrdenados = [...modulo.topicos].sort((a, b) => a.ordem - b.ordem)
    for (const topico of topicosOrdenados) {
      if (topico.status === 'em-andamento') return topico.id
    }
  }

  for (const modulo of modulosOrdenados) {
    const topicosOrdenados = [...modulo.topicos].sort((a, b) => a.ordem - b.ordem)
    for (const topico of topicosOrdenados) {
      if (topico.status === 'nao-iniciado') return topico.id
    }
  }

  return null
}

export function computeChecklistPendentes(
  progressoAll: TopicoProgresso[],
  modulos: ModuloChecklist[],
): {
  modulos: ModuloChecklistPendente[]
  totalItens: number
  totalPassos: number
} {
  const progressoMap = new Map(progressoAll.map((p) => [p.topicoId, p]))
  const passoAtualTopicoId = getPassoAtualTopicoId(modulos)
  const allPassos = getAllPassos()
  const passoAtual = passoAtualTopicoId
    ? allPassos.find((p) => p.topicoId === passoAtualTopicoId)
    : null
  const limiteIndex = passoAtual?.globalIndex ?? Infinity

  const passosComPendencia: PassoChecklistPendente[] = []

  for (const passo of allPassos) {
    if (!passo.temChecklist) continue
    if (passo.globalIndex >= limiteIndex) continue

    const contentItems = getTopicoContent(passo.topicoId)?.checklistAplicabilidade ?? []
    if (contentItems.length === 0) continue

    const merged = mergeChecklist(
      contentItems,
      progressoMap.get(passo.topicoId)?.checklistItems ?? [],
    )
    const pendentes = merged.filter((i) => !i.concluido)
    if (pendentes.length === 0) continue

    const concluidos = merged.length - pendentes.length
    passosComPendencia.push({
      passo,
      itensPendentes: pendentes.map(({ id, texto }) => ({ id, texto })),
      total: merged.length,
      concluidos,
      percentual: merged.length > 0 ? Math.round((concluidos / merged.length) * 100) : 0,
    })
  }

  const porModulo = new Map<string, ModuloChecklistPendente>()

  for (const item of passosComPendencia) {
    const { moduloId, moduloTitulo, moduloIcone, moduloOrdem } = item.passo
    const existing = porModulo.get(moduloId)
    if (existing) {
      existing.passos.push(item)
      existing.totalPendentes += item.itensPendentes.length
    } else {
      porModulo.set(moduloId, {
        moduloId,
        moduloTitulo,
        moduloIcone,
        moduloOrdem,
        passos: [item],
        totalPendentes: item.itensPendentes.length,
      })
    }
  }

  const modulosResult = [...porModulo.values()].sort((a, b) => a.moduloOrdem - b.moduloOrdem)
  const totalItens = modulosResult.reduce((s, m) => s + m.totalPendentes, 0)
  const totalPassos = passosComPendencia.length

  return { modulos: modulosResult, totalItens, totalPassos }
}
